import { Directive, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

/**
 * Sentinel-based infinite scroll. Place an empty element at the bottom of a
 * list; `reached` fires when it comes within `margin` px of the viewport
 * (IntersectionObserver — no scroll-height math, works in any container).
 *
 * Bind `busy` to the list's loading flag: when loading finishes and the
 * sentinel is still in view (page didn't fill the screen), `reached` fires
 * again so the next page loads without a scroll event.
 *
 * When the list scrolls inside its own container, pass that container as
 * `root`. `rootMargin` only expands the root's rect — against the default
 * (the viewport) an intervening scroll container still clips the sentinel, so
 * the margin would be silently dead and pages would only load at the very
 * bottom.
 */
@Directive({
  selector: '[appInfiniteScrollTrigger]',
  standalone: true,
})
export class InfiniteScrollTriggerDirective implements OnInit, OnChanges, OnDestroy {
  @Input() busy = false;
  @Input() margin = 400;
  /** Scroll container the sentinel lives in; omit when the page itself scrolls */
  @Input() root?: HTMLElement;
  @Output() reached = new EventEmitter<void>();

  private observer?: IntersectionObserver;
  private visible = false;

  constructor(
    private el: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {}

  ngOnInit() {
    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          this.visible = entries.some((e) => e.isIntersecting);
          if (this.visible) this.zone.run(() => this.reached.emit());
        },
        { root: this.root ?? null, rootMargin: `${this.margin}px` },
      );
      this.observer.observe(this.el.nativeElement);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Loading just finished → if the sentinel is still on screen, ask for more.
    // Deferred: emitting synchronously from ngOnChanges mutates the host's
    // loading flag mid-change-detection (NG0100).
    if (changes.busy && changes.busy.previousValue === true && !this.busy && this.visible) {
      Promise.resolve().then(() => {
        if (this.visible && !this.busy) this.reached.emit();
      });
    }
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }
}
